import { useCallback, useEffect, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

/**
 * Moves a piece of UI into a real operating-system window and back.
 *
 * Two mechanisms, because no single one works everywhere:
 *
 *   Document Picture-in-Picture gives a small always-on-top window with no
 *   browser chrome — exactly what a call belongs in — but only Chromium has
 *   it.
 *
 *   A popup window (`window.open`) is the fallback. It has a title bar and is
 *   not always-on-top, but it is still a separate window the user can put on
 *   another monitor, and it works in Firefox.
 *
 * Either way the content is *moved*, not copied: it is rendered through a
 * portal into the other document, so the React state, the LiveKit room and
 * the media tracks are the same objects as before. Nothing reconnects.
 */
export interface PopoutOptions {
  /** Window title, shown by the popup fallback. */
  title: string;
  /** Distinct per panel, or the popup fallback reuses one window for both. */
  name: string;
  width: number;
  height: number;
}

interface DocumentPictureInPicture {
  requestWindow(options?: { width?: number; height?: number }): Promise<Window>;
  readonly window: Window | null;
}

function pipApi(): DocumentPictureInPicture | null {
  const api = (globalThis as { documentPictureInPicture?: DocumentPictureInPicture })
    .documentPictureInPicture;
  return api && typeof api.requestWindow === 'function' ? api : null;
}

/**
 * Styles do not follow an element into another document, so every stylesheet
 * is copied across. Adopted stylesheets cover Vite's dev-time injection and
 * anything CSS-in-JS; `<link>`/`<style>` elements cover the built bundle.
 */
function copyStyles(target: Window): void {
  const head = target.document.head;
  try {
    target.document.adoptedStyleSheets = [...document.adoptedStyleSheets];
  } catch {
    // Cross-document adoption is not allowed everywhere; the clones below
    // are enough on their own.
  }
  for (const node of document.querySelectorAll('style, link[rel="stylesheet"]')) {
    head.appendChild(node.cloneNode(true));
  }
  // The call UI is styled against the page's custom properties, which live on
  // :root and on the theme attribute — both of which are on <html>, not in a
  // stylesheet.
  for (const attr of document.documentElement.getAttributeNames()) {
    const value = document.documentElement.getAttribute(attr);
    if (value !== null) target.document.documentElement.setAttribute(attr, value);
  }
  target.document.body.classList.add('popout-body');
}

export interface PopoutWindow {
  /** True while the content lives in its own OS window. */
  open: boolean;
  /** Whether this browser can pop out at all — false blocks the button. */
  supported: boolean;
  requestOpen: () => void;
  close: () => void;
  /** Wraps the content: a portal while popped out, the children as-is otherwise. */
  render: (children: ReactNode) => ReactNode;
}

export function usePopoutWindow(options: PopoutOptions): PopoutWindow {
  const { title, name, width, height } = options;
  const [target, setTarget] = useState<Window | null>(null);

  const close = useCallback(() => {
    setTarget((current) => {
      current?.close();
      return null;
    });
  }, []);

  const requestOpen = useCallback(() => {
    const open = async () => {
      // A browser allows exactly one document picture-in-picture window, and
      // asking for a second one takes the first away. So the second panel to
      // be detached — usually the chat, after the call — gets a popup rather
      // than silently pulling the call back into the page.
      const pip = pipApi();
      const opened =
        pip && pip.window === null
          ? await pip.requestWindow({ width, height })
          : window.open('', name, `popup=yes,width=${width},height=${height}`);
      if (!opened) return;

      opened.document.title = title;
      copyStyles(opened);
      // Closing the window is the same as pressing "return to page", so the
      // in-page window comes back instead of the content vanishing.
      opened.addEventListener('pagehide', () => setTarget(null));
      setTarget(opened);
    };
    // A blocked popup or a rejected PiP request should leave the call alone.
    void open().catch(() => setTarget(null));
  }, [height, name, title, width]);

  useEffect(() => {
    if (!target) return;
    return () => target.close();
  }, [target]);

  const render = useCallback(
    (children: ReactNode) =>
      target ? createPortal(<div className="popout-root">{children}</div>, target.document.body) : children,
    [target],
  );

  return {
    open: target !== null,
    supported: pipApi() !== null || typeof window.open === 'function',
    requestOpen,
    close,
    render,
  };
}
